/**
 * Модалка создания нового сотрудника.
 * Позволяет заполнить личные данные, роли и выбрать подразделение.
 * Если выбрано КБ, оно сохраняется в kbIds. Если выбран отдел, 
 * он сохраняется в departments (с флагом primary: true).
 */
import { useCallback, useMemo, useState } from "react";
import { ModalShell } from "../ModalShell";
import { EmployeeFormFields } from "../EmployeeFormFields";
import { useModalForm } from "../../hooks/useModalForm";
import {
  useCreateEmployeeMutation,
  useEmployeesQuery,
} from "../../api/queries/employees";
import { useDepartmentsQuery } from "../../api/queries/departments";
import { useKbsQuery } from "../../api/queries/kbs";
import { uid } from "../../utils/date";
import { ROLES } from "../../utils/constants";
import { FormField } from "../FormField";

const FIELDS = Object.freeze([
  "last",
  "first",
  "email",
  "pass",
  "position",
  "extension",
  "tab",
  "roles",
  "unitId",
]);

export const CreateEmployeeModal = ({ onClose, toast }) => {
  const employeesQ = useEmployeesQuery();
  const departmentsQ = useDepartmentsQuery();
  const kbsQ = useKbsQuery();
  const createMutation = useCreateEmployeeMutation();

  const employees = useMemo(() => employeesQ.data ?? [], [employeesQ.data]);
  const departments = useMemo(() => departmentsQ.data ?? [], [departmentsQ.data]);
  const kbs = useMemo(() => kbsQ.data ?? [], [kbsQ.data]);

  const [initialTab] = useState(() =>
    String(1000 + Math.floor(Math.random() * 8999)),
  );

  const unitOptions = useMemo(() => {
    return [
      { value: "none", label: "Вне подразделений" },
      ...kbs.map((kb) => ({ value: kb.id, label: `КБ: ${kb.name}` })),
      ...departments.map((d) => ({ value: d.id, label: `Отдел: ${d.name}` })),
    ];
  }, [kbs, departments]);

  const initialValues = useMemo(
    () => ({
      last: "",
      first: "",
      email: "",
      pass: "",
      position: "Сотрудник",
      extension: "",
      tab: initialTab,
      roles: ["executor"],
      unitId: "none",
    }),
    [initialTab],
  );

  const validate = useCallback(
    (values) => {
      const errors = {};
      if (!values.last.trim()) errors.last = "Фамилия обязательна";
      if (!values.first.trim()) errors.first = "Имя обязательно";
      if (!values.email.trim()) errors.email = "E-mail обязателен";
      if (!values.pass.trim()) errors.pass = "Пароль обязателен";
      if (values.pass.length < 8)
        errors.pass = "Пароль должен быть не менее 8 символов";
      if (employees.some((e) => e.email === values.email)) {
        errors.email = "Сотрудник с таким email уже существует";
      }
      return errors;
    },
    [employees],
  );

  const saveAsync = useCallback(
    async (vals) => {
      const isKb = vals.unitId.startsWith("kb_");
      const isDept = vals.unitId.startsWith("d_");

      const departmentsPayload = isDept
        ? [{ deptId: vals.unitId, primary: true, position: vals.position, rate: "1.0" }]
        : [];
      const kbIdsPayload = isKb ? [vals.unitId] : [];

      const id = "e_" + uid();
      await createMutation.mutateAsync({
        id,
        email: vals.email.trim(),
        first: vals.first.trim(),
        last: vals.last.trim(),
        position: vals.position.trim() || "Сотрудник",
        extension: vals.extension || "",
        tabNumber: vals.tab || null,
        roles: vals.roles,
        kbIds: kbIdsPayload,
        departments: departmentsPayload,
        password: vals.pass,
      });
      onClose();
    },
    [createMutation, onClose],
  );

  const { values, handleChange, errors, touched, handleSubmit, isSubmitting } =
    useModalForm(
      initialValues,
      validate,
      saveAsync,
      (error) => {
        toast(error.message || "Ошибка создания сотрудника", "error");
      },
      { fields: FIELDS },
    );

  const roleOptions = Object.entries(ROLES).map(([k, v]) => ({
    value: k,
    label: v.label,
  }));

  return (
    <ModalShell
      title="Добавить сотрудника"
      onClose={onClose}
      onSave={handleSubmit()}
      saveLabel="Создать"
      width={560}
      className="modal-employee"
      saveDisabled={isSubmitting}
    >
      <EmployeeFormFields
        values={values}
        onChange={handleChange}
        errors={errors}
        touched={touched}
        passwordRequired
      />
      
      <div className="mt-3">
        <FormField
          label="Подразделение"
          type="select"
          options={unitOptions}
          value={values.unitId}
          onChange={(v) => handleChange("unitId", v)}
        />
      </div>

      <div className="mt-3">
        <FormField
          label="Роли"
          type="select"
          options={roleOptions}
          value={values.roles}
          onChange={(v) => handleChange("roles", v)}
          multiple
        />
      </div>
    </ModalShell>
  );
};
